import { Dispatch } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import type { RootState } from '../store/store';
import {
  clearAssociadoData,
  setAssociadoId,
  setRascunhoId,
  updateAssociadoData,
} from '../store/associadoSlice';
import { resetCodigoData, setCodigoData } from '../store/codigoSlice';
import { resetRespostasDoSga, setTermoInfo } from '../store/respostasDoSgaSlice';

import {
  AtualizarRascunhoInput,
  ErroRascunhoAdesao,
  EtapaRascunhoAdesao,
  PayloadRascunhoAdesaoV1,
  RascunhoAdesaoCompleto,
  StatusRascunhoAdesao,
  atualizarRascunho,
  concluirRascunho,
  criarRascunho,
} from '../api/rascunho-adesao/rascunhoAdesaoApi';

// Só o TIPO da variante do chip (apagado na compilação) — o rótulo de status é
// regra de domínio e mora aqui, mas quem pinta é o design system.
import type { VarianteChip } from '../ui/primitivos/Chip';

import {
  ASSOCIADO,
  ASSOCIADOEDIT,
  ASSOCIADOSUBSTITUIR,
} from '../views/associado/routes';
import { CARRO, CARROEDIT, CARROSUBSTITUIR } from '../views/carro/routes';
import { DOCUMENTO, DOCUMENTO_EDIT } from '../views/documento/routes';
import { PRODUTO } from '../views/produto/routes';
import { TERMO } from '../views/termo/routes';
import { ASSINATURA } from '../views/assinatura/routes';

/**
 * Camada de fluxo do rascunho de adesão (at-38, item 22).
 *
 * Fonte ÚNICA de tudo que as telas do fluxo fazem com o rascunho — precedente
 * do `vistoriaFluxo.ts` (at-24/at-32). As telas chamam daqui e nunca a API do
 * rascunho direto: é aqui que mora a garantia de que **falha de rascunho não
 * interrompe a adesão** (D8) e a regra de o que entra no snapshot.
 */

/** Só dígitos — o CPF circula mascarado nas telas e o backend indexa por dígitos. */
const somenteDigitos = (valor?: string | null): string => (valor || '').replace(/\D/g, '');

/**
 * Snapshot do fluxo, no formato interno dos slices.
 *
 * ⚠️ Nada de `auth` (token/PII do consultor) e nada de base64 de documento.
 * O `rascunhoId` é retirado de propósito: ele identifica a linha, não é dado do
 * fluxo — mantê-lo dentro do payload faria a reidratação disputar com o
 * `setRascunhoId` autoritativo.
 */
export function montarPayloadRascunho(state: RootState): PayloadRascunhoAdesaoV1 {
  const { rascunhoId, ...associado } = state.associado as unknown as Record<
    string,
    unknown
  > & { rascunhoId?: string | null };

  return {
    versao: 1,
    associado,
    codigo: {
      codigoRegional: state.codigo.codigoRegional,
      codigoCooperativa: state.codigo.codigoCooperativa,
      codigoTipoVeiculo: state.codigo.codigoTipoVeiculo,
      valorFipe: state.codigo.valorFipe,
    },
    respostasDoSga: {
      codigoAssociado: state.respostasDoSga.codigoAssociado,
      codigoVeiculo: state.respostasDoSga.codigoVeiculo,
      codigoEmissao: state.respostasDoSga.codigoEmissao,
      codigoTermo: state.respostasDoSga.codigoTermo,
    },
  };
}

/**
 * Grava a etapa concluída — **nunca lança e nunca bloqueia o fluxo** (D8).
 *
 * Cria o rascunho na primeira etapa e atualiza nas seguintes. O estado tem de
 * ser lido com `getState()` **depois** do `dispatch` da tela: o valor do
 * `useSelector` da renderização anterior ainda não tem o que acabou de entrar.
 *
 * `extra.associadoId` é passado na etapa `PRODUTO` (logo após o cadastro); o
 * backend resolve o `carroId` a partir dele.
 */
export async function salvarEtapaRascunho(
  dispatch: Dispatch,
  getState: () => RootState,
  etapa: EtapaRascunhoAdesao,
  extra?: { associadoId?: string | null },
): Promise<void> {
  try {
    const state = getState();
    const cpfCnpj = somenteDigitos(state.associado.cpfCnpj);
    const nomeCompleto = (state.associado.nomeCompleto || '').trim();

    // Sem CPF/nome não há resumo para listar nem chave de deduplicação (D6) —
    // e o backend recusaria. Silêncio aqui é correto: não é falha do consultor.
    if (!cpfCnpj || !nomeCompleto) return;

    const payload = montarPayloadRascunho(state);
    const placa = (state.associado.carro?.placa || '').trim() || undefined;
    const idExistente = state.associado.rascunhoId;

    if (idExistente) {
      const patch: AtualizarRascunhoInput = { etapa, payload, nomeCompleto };
      if (placa) patch.placa = placa;
      if (extra?.associadoId) patch.associadoId = extra.associadoId;

      await atualizarRascunho(idExistente, patch);
      return;
    }

    const criado = await criarRascunho({ cpfCnpj, nomeCompleto, placa, etapa, payload });
    dispatch(setRascunhoId(criado.id));

    // O POST não recebe `associadoId` (contrato). Quando a primeira gravação é
    // justamente a do pós-cadastro (o rascunho das etapas anteriores falhou),
    // um PATCH complementar amarra o rascunho ao registro criado — sem isso a
    // retomada em PRODUTO/TERMO abriria sem `associadoId` e travaria.
    if (extra?.associadoId) {
      await atualizarRascunho(criado.id, { associadoId: extra.associadoId });
    }
  } catch (erro) {
    const status = (erro as ErroRascunhoAdesao)?.status ?? null;

    // 409 = concluído/descartado em outro dispositivo (D9). Não é erro do
    // consultor: solta o id local e o próximo passo cria um rascunho novo.
    if (status === 409) {
      dispatch(setRascunhoId(null));
      toast.info(
        'Este rascunho foi concluído ou descartado em outro dispositivo. Um novo será criado no próximo passo.',
        { autoClose: 6000 },
      );
      return;
    }

    toast.warn(
      'Não foi possível salvar o rascunho desta etapa. O cadastro continua normalmente.',
      { toastId: 'rascunho-adesao-falha', autoClose: 6000 },
    );
  }
}

/**
 * Fecha o rascunho no fim do fluxo (`/assinatura`) — **nunca lança**.
 *
 * O backend zera o `payload` (PII) e mantém só o resumo para o histórico (D4).
 * Um 409 aqui significa que outro dispositivo já encerrou: desfecho idêntico ao
 * desejado, então não vira aviso nenhum.
 */
export async function concluirRascunhoDoFluxo(
  dispatch: Dispatch,
  getState: () => RootState,
): Promise<void> {
  const id = getState().associado.rascunhoId;
  if (!id) return;

  try {
    await concluirRascunho(id);
  } catch (erro) {
    const status = (erro as ErroRascunhoAdesao)?.status ?? null;
    if (status !== 409 && status !== 404) {
      // Não há o que o consultor faça a respeito, e a adesão está concluída —
      // avisar com erro vermelho aqui seria alarme falso no fim do trabalho.
      toast.info(
        'A adesão foi concluída. O rascunho pode continuar aparecendo em "Em andamento" — descarte-o pela lista.',
        { autoClose: 7000 },
      );
    }
  } finally {
    // O fluxo terminou: este id não serve mais para nada (e `/concluido` limpa
    // o resto do estado ao montar).
    dispatch(setRascunhoId(null));
  }
}

/**
 * Reidrata o fluxo a partir do rascunho — do zero, na ordem: limpa os três
 * slices e só então aplica o snapshot.
 *
 * A limpeza antes não é zelo: sem ela o merge raso do `updateAssociadoData`
 * deixaria campos de uma adesão anterior sobrevivendo dentro desta.
 */
export function hidratarFluxoAdesao(
  dispatch: Dispatch,
  rascunho: RascunhoAdesaoCompleto,
): void {
  dispatch(clearAssociadoData());
  dispatch(resetCodigoData());
  dispatch(resetRespostasDoSga());

  const payload = rascunho.payload;

  if (payload?.associado) {
    // Merge raso do objeto inteiro: entram `carro` e os campos de endereço da
    // raiz do slice (que são os que as telas de fato leem).
    dispatch(updateAssociadoData(payload.associado as any));
  }

  if (payload?.codigo) {
    dispatch(
      setCodigoData({
        codigoRegional: payload.codigo.codigoRegional ?? '',
        codigoCooperativa: payload.codigo.codigoCooperativa ?? '',
        codigoTipoVeiculo: payload.codigo.codigoTipoVeiculo ?? '',
        valorFipe: payload.codigo.valorFipe ?? '',
      }),
    );
  }

  const respostas = payload?.respostasDoSga;
  if (respostas?.codigoTermo || respostas?.codigoEmissao) {
    dispatch(
      setTermoInfo({
        codigoEmissao: respostas.codigoEmissao ?? '',
        codigoTermo: respostas.codigoTermo ?? '',
      }),
    );
  }

  // Depois do snapshot: são os valores autoritativos da linha do rascunho.
  if (rascunho.associadoId) dispatch(setAssociadoId(rascunho.associadoId));
  dispatch(setRascunhoId(rascunho.id));
}

export interface DestinoRetomada {
  rota: string;
  /** Só a etapa ASSINATURA precisa de `location.state` (a tela lê dali). */
  state?: Record<string, unknown>;
}

/** Etapa → tela de retomada (plano 0, §3). */
export function rotaDaEtapa(rascunho: RascunhoAdesaoCompleto): DestinoRetomada {
  switch (rascunho.etapa) {
    case 'CARRO':
      return { rota: CARRO() };
    case 'DOCUMENTO':
      return { rota: DOCUMENTO() };
    case 'PRODUTO':
      return { rota: PRODUTO() };
    case 'TERMO':
      return { rota: TERMO() };
    case 'ASSINATURA':
      return {
        rota: ASSINATURA(),
        state: {
          // A tela `/termo` navega com `codigoEmissao` vazio (termo LOCAL não
          // tem código de emissão — `TermoLocal` não guarda esse campo), então
          // a retomada reproduz o mesmo valor em vez de inventar um.
          codigoEmissao: rascunho.payload?.respostasDoSga?.codigoEmissao ?? '',
          codigoTermo: rascunho.payload?.respostasDoSga?.codigoTermo ?? '',
          associadoId: rascunho.associadoId,
          carroId: rascunho.carroId,
        },
      };
    default:
      // Etapa desconhecida (backend novo, front antigo): a primeira tela pós
      // associado é sempre segura — o snapshot já está hidratado.
      return { rota: CARRO() };
  }
}

const ROTULOS_ETAPA: Record<EtapaRascunhoAdesao, string> = {
  CARRO: 'Veículo',
  DOCUMENTO: 'Documentos',
  PRODUTO: 'Produtos',
  TERMO: 'Termo',
  ASSINATURA: 'Assinatura',
};

/** Texto do chip de etapa: o que **falta fazer**, não o que já foi feito. */
export function rotuloEtapa(etapa: EtapaRascunhoAdesao | string): string {
  return ROTULOS_ETAPA[etapa as EtapaRascunhoAdesao] ?? String(etapa);
}

const ROTULOS_STATUS: Record<StatusRascunhoAdesao, { texto: string; variante: VarianteChip }> = {
  EM_ANDAMENTO: { texto: 'Em andamento', variante: 'pendente' },
  CONCLUIDO: { texto: 'Concluído', variante: 'ok' },
  DESCARTADO: { texto: 'Descartado', variante: 'neutro' },
};

/**
 * Texto e cor do chip de status. As cores espelham as da carteira/envios
 * (âmbar = em curso, verde = feito, cinza = fora de jogo) — a regra mora aqui,
 * num lugar só, e o componente apenas renderiza (convenção da at-24).
 *
 * ⚠️ **at-56:** devolvia uma `classe` do CSS desta aba (`.rascunho-chip--ok`) e
 * passou a devolver a **variante do `Chip`** do design system. A regra continua
 * aqui de propósito — mover o mapa para dentro do componente seria justamente a
 * duplicação que a convenção da at-24 proíbe.
 */
export function rotuloStatus(status: StatusRascunhoAdesao | string): {
  texto: string;
  variante: VarianteChip;
} {
  return (
    ROTULOS_STATUS[status as StatusRascunhoAdesao] ?? {
      texto: String(status),
      variante: 'neutro',
    }
  );
}

// ── As bolinhas do fluxo (at-62) ───────────────────────────────────────────
//
// 🔴 Pedido de Victor (10/09/2026), depois de ver o mesmo na cotação: as
// bolinhas do fluxo de adesão precisam **dizer a verdade** sobre as etapas
// anteriores e **levar** o consultor até elas. Toda a regra mora aqui, num lugar
// só; o `Stepper` do kit apenas renderiza (convenção at-24/at-32/at-47).

/**
 * Os TRÊS fluxos que passam pelas mesmas 6 etapas (`plan-c` §1.4).
 *
 * ⚠️ Eles só se distinguem nas etapas **1 a 3**: a cauda (`/produto`, `/termo`,
 * `/assinatura`) é literalmente a mesma tela nos três.
 */
export type FluxoDeAdesao = 'ADESAO' | 'SUBSTITUICAO_VEICULAR' | 'SUBSTITUICAO_TITULAR';

/** Etapa (1-based) → rota, por fluxo. `null` = a etapa não é alcançável por link. */
const ROTAS_POR_FLUXO: Record<FluxoDeAdesao, readonly (string | null)[]> = {
  ADESAO: [ASSOCIADO(), CARRO(), DOCUMENTO(), PRODUTO(), TERMO(), null],
  SUBSTITUICAO_VEICULAR: [
    ASSOCIADOEDIT(),
    CARROEDIT(),
    DOCUMENTO_EDIT(),
    PRODUTO(),
    TERMO(),
    null,
  ],
  SUBSTITUICAO_TITULAR: [
    ASSOCIADOSUBSTITUIR(),
    CARROSUBSTITUIR(),
    DOCUMENTO_EDIT(),
    PRODUTO(),
    TERMO(),
    null,
  ],
};

/**
 * Para onde a bolinha da etapa leva, dentro do fluxo em curso.
 *
 * ⚠️ **`fluxo` nulo = fluxo desconhecido**, e é o caso das telas da CAUDA
 * (`/produto`, `/termo`, `/assinatura`) e de `/documento/edit`: elas servem mais
 * de um fluxo e não têm como saber qual. Ali só as etapas compartilhadas (4 e 5)
 * têm destino certo. Inferir pelos marcadores do carro (`codigoAssociadoNovo`,
 * `placaSubstituido`) é justamente a inferência que a at-54 registrou como **não
 * exclusiva** — mandar um consultor em substituição para o formulário de adesão
 * nova é pior que não deixá-lo navegar.
 *
 * ⚠️ **A etapa 6 nunca tem rota.** `/assinatura` depende de um `location.state`
 * (`codigoTermo`, `associadoId`, `carroId`) que só `/termo` sabe montar — a at-60
 * corrigiu justamente o caso de abri-la sem esse estado.
 */
export function rotaDaEtapaDoFluxo(
  fluxo: FluxoDeAdesao | null,
  etapa: number,
): string | null {
  if (etapa < 1 || etapa > 6) return null;
  if (fluxo) return ROTAS_POR_FLUXO[fluxo][etapa - 1] ?? null;

  // Fluxo desconhecido: só o que é igual nos três.
  return etapa === 4 ? PRODUTO() : etapa === 5 ? TERMO() : null;
}

const preenchido = (valor?: string | number | null): boolean =>
  typeof valor === 'number' ? valor !== 0 : Boolean((valor ?? '').toString().trim());

/**
 * Só as fatias do estado que as bolinhas leem.
 *
 * ⚠️ Recorte deliberado, e não `RootState`: é o que permite ao componente
 * selecionar **duas** fatias em vez do store inteiro — com `RootState` ele
 * re-renderizaria a cada mudança de qualquer slice do app.
 */
type EstadoDoFluxo = Pick<RootState, 'associado' | 'respostasDoSga'>;

/**
 * O que o CADASTRO precisa de cada etapa.
 *
 * 🔴 **A referência é a DTO do backend, não a validação do formulário.** Cada
 * campo checado aqui é `@IsNotEmpty` em `CreateAssociadoDto` (conferido em
 * 10/09/2026): se falta um, o `POST /associado/cadastrar` **vai** ser recusado.
 * É isso que autoriza a etapa 3 a **bloquear** com base nesta função — ser um
 * subconjunto do que o servidor exige é o que garante que nada legítimo é
 * barrado. Um campo esquecido aqui só devolve o erro genérico de antes; um campo
 * a mais barraria cadastro válido.
 *
 * ⚠️ Não é a mesma pergunta do formulário. `FormularioAssociado`/
 * `FormularioVeiculo` decidem *"posso salvar esta tela?"* (e cobram também o que
 * a DTO aceita vazio); aqui a pergunta é *"o cadastro seria aceito?"*.
 *
 * 📌 Na prática as etapas 1 e 2 quase nunca aparecem pendentes — não se avança
 * sem salvar, e salvar exige o formulário completo. O vermelho aqui é **rede de
 * segurança** para os estados atípicos: rascunho retomado de uma versão antiga
 * do slice, ou alguém chegando à etapa 3 pela régua sem ter preenchido nada. É
 * exatamente isso que faz o indicador ser confiável: ele para de **afirmar** o
 * que nunca conferiu.
 */
function etapaIncompleta(state: EstadoDoFluxo, etapa: number): boolean {
  const associado = state.associado;
  const carro = associado.carro;

  switch (etapa) {
    case 1:
      return !(
        preenchido(associado.cpfCnpj) &&
        preenchido(associado.nomeCompleto) &&
        preenchido(associado.rg) &&
        preenchido(associado.dataNascimento) &&
        /* ⚠️ `views/documento` faz `associado.sexo.toUpperCase()` **sem `?.`**:
           sem sexo a tela nem chega a chamar o backend — estoura antes e cai no
           mesmo toast genérico. */
        preenchido(associado.sexo) &&
        preenchido(associado.email) &&
        preenchido(associado.dddCelular) &&
        preenchido(associado.celular) &&
        preenchido(associado.regionalId) &&
        // O endereço vive na RAIZ do slice (é o que `views/documento` envia);
        // `endereco` é o objeto legado que sobreviveu ao lado.
        preenchido(associado.rua ?? associado.endereco?.rua) &&
        preenchido(associado.numero ?? associado.endereco?.numero) &&
        preenchido(associado.bairro ?? associado.endereco?.bairro) &&
        preenchido(associado.cidade ?? associado.endereco?.cidade) &&
        preenchido(associado.estado ?? associado.endereco?.estado) &&
        preenchido(associado.cep ?? associado.endereco?.cep)
      );

    case 2: {
      if (!carro) return true;
      const ehBike = preenchido(carro.bikeEletricaId);

      const comuns =
        preenchido(carro.modelo) &&
        preenchido(carro.anoModelo) &&
        preenchido(carro.anoFabricacao) &&
        preenchido(carro.tipoVeiculoId) &&
        preenchido(carro.cooperativaId) &&
        preenchido(carro.cotaId) &&
        preenchido(carro.vencimentoDesejado) &&
        preenchido(carro.endereco?.rua) &&
        preenchido(carro.endereco?.numero) &&
        preenchido(carro.endereco?.bairro) &&
        preenchido(carro.endereco?.cidade) &&
        preenchido(carro.endereco?.estado) &&
        preenchido(carro.endereco?.cep);

      /*
       * Motocicleta elétrica (at-46): a DTO relaxa placa, chassi, renavam,
       * câmbio, categoria e código FIPE com `@ValidateIf(naoEhBike)` — o backend
       * os preenche. Em troca, `valorProtegido` passa a ser exigido só nela.
       */
      // at-81: 0km também relaxa placa e renavam na DTO — o chassi o identifica.
      const zeroKm = carro.zeroKm === true;
      const proprios = ehBike
        ? preenchido(carro.valorProtegido)
        : (zeroKm || preenchido(carro.placa)) &&
          preenchido(carro.chassi) &&
          (zeroKm || preenchido(carro.renavam)) &&
          preenchido(carro.corId) &&
          preenchido(carro.combustivelId) &&
          preenchido(carro.cambioId) &&
          preenchido(carro.categoriaId) &&
          preenchido(carro.codigoFipe);

      return !(comuns && proprios);
    }

    // O cadastro é o que a etapa 3 produz. `associadoId` é o mesmo marcador que
    // a at-60 usa para o estado vazio de `/produto`.
    case 3:
      return !preenchido(associado.associadoId);

    // at-60: `/produto` passou a despachar o valor de adesão SEMPRE (antes só no
    // `onChange`, então quem aceitava os 350 gerava o termo com o campo vazio).
    case 4:
      return !preenchido(carro?.valorAdesao);

    // `/termo` grava o código do TIPO escolhido ao gerar (at-40).
    case 5:
      return !preenchido(state.respostasDoSga?.codigoTermo);

    default:
      return false;
  }
}

/**
 * Pendências e travas das 6 bolinhas, a partir do estado do fluxo.
 *
 * 🔴 **A trava das etapas 1 a 3 depois do cadastro é o ponto mais importante
 * daqui, e não é conservadorismo — são dois defeitos evitados:**
 *
 *   1. **409 garantido.** `views/documento` chama `createAssociado()` sempre, sem
 *      guarda para associado já criado: reabrir a etapa 3 e salvar devolve
 *      "CPF já cadastrado".
 *   2. **Contrato divergindo do cadastro.** `/termo` monta o documento lendo o
 *      **slice**, não o banco. Reabrir a etapa 1 e mudar o e-mail depois do
 *      cadastro mudaria o termo e **não** o registro — o PDF passaria a afirmar
 *      um dado que o cadastro não tem.
 *
 * Antes do cadastro, as três primeiras são livres entre si.
 *
 * ⚠️ **Pendência só é reportada para etapa ATRÁS da atual.** Etapa à frente está
 * incompleta por definição — pintá-la de vermelho seria alarme sobre o futuro,
 * exatamente o ruído que a at-55 tirou do `PendenciasBanner`.
 *
 * ⚠️ **Navegar descarta o que não foi salvo**, igual ao botão "Voltar" que as
 * at-58/59/60 acrescentaram a estas telas (o formulário só escreve no slice no
 * submit, e as fotos da etapa 3 vivem em estado local). É o mesmo desfecho do
 * gesto que já existia, não um risco novo.
 */
export function situacaoDasEtapasDaAdesao(
  state: EstadoDoFluxo,
  etapaAtual: number,
): { pendentes: number[]; desabilitadas: Record<number, string> } {
  const cadastrado = preenchido(state.associado.associadoId);

  const pendentes: number[] = [];
  for (let etapa = 1; etapa < etapaAtual; etapa += 1) {
    if (etapaIncompleta(state, etapa)) pendentes.push(etapa);
  }

  const desabilitadas: Record<number, string> = {};

  if (cadastrado) {
    const motivo = 'já cadastrado, não pode ser refeita';
    [1, 2, 3].forEach((etapa) => {
      if (etapa !== etapaAtual) desabilitadas[etapa] = motivo;
    });
  } else {
    // Sem cadastro, a cauda não tem o que mostrar (a at-60 deu a ela estados
    // vazios explicando isso) — deixar clicável seria oferecer um beco sem saída.
    [4, 5].forEach((etapa) => {
      if (etapa !== etapaAtual) desabilitadas[etapa] = 'disponível depois do cadastro';
    });

    // A etapa 3 cadastra: sem o titular e o veículo completos, ela recusaria.
    if (etapaAtual !== 3 && (etapaIncompleta(state, 1) || etapaIncompleta(state, 2))) {
      desabilitadas[3] = 'conclua o titular e o veículo primeiro';
    }
  }

  // A etapa 6 nunca é alcançável por link — ver `rotaDaEtapaDoFluxo`.
  if (etapaAtual !== 6) desabilitadas[6] = 'abre ao gerar o termo';

  return { pendentes, desabilitadas };
}

/**
 * Começo de um fluxo NOVO de adesão.
 *
 * Corrige o estado residual entre adesões: a opção "Adesões" da Home não
 * limpava nada e `/concluido` também não, então o formulário seguinte herdava
 * o que sobrou do anterior (o submit faz merge raso). Também garante que a
 * adesão nova não continue amarrada ao rascunho da anterior.
 */
export function iniciarNovaAdesao(dispatch: Dispatch): void {
  dispatch(clearAssociadoData());
  dispatch(resetCodigoData());
  dispatch(resetRespostasDoSga());
}
